import { type ComponentProps, type ComponentPropsWithoutRef, type ElementRef, forwardRef, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { OptionHorizontalIcon, OptionVerticalIcon } from '@/icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/ui';
import { IconButton } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
type DropdownContentAlign = ComponentProps<typeof DropdownMenuContent>['align'];
type IconButtonProps = ComponentPropsWithoutRef<typeof IconButton>;
type IconButtonElement = ElementRef<typeof IconButton>;
const MENU_CONTENT_CLASS_NAME = 'min-w-[160px] rounded-sm border border-assistive bg-default px-xs py-3xs shadow-lg';
const MENU_ITEM_CLASS_NAME = 'flex h-[40px] min-h-[40px] items-center rounded-sm px-2xs py-4xs';
function stopMenuEvent(event: {
  stopPropagation: () => void;
}) {
  event.stopPropagation();
}
interface ClassroomDropdownMenuProps {
  children: ReactNode;
  align?: DropdownContentAlign;
  contentClassName?: string;
  trigger?: ReactNode;
}
export function ClassroomDropdownMenu({
  align = 'end',
  children,
  contentClassName,
  trigger
}: ClassroomDropdownMenuProps) {
  return <DropdownMenu modal={false} data-sentry-element="DropdownMenu" data-sentry-component="ClassroomDropdownMenu" data-sentry-source-file="ClassroomDropdownMenu.tsx">
      <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="ClassroomDropdownMenu.tsx">
        {trigger ?? <ClassroomDropdownMenuTrigger />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} onPointerDown={stopMenuEvent} onClick={stopMenuEvent} className={cn(MENU_CONTENT_CLASS_NAME, contentClassName)} data-sentry-element="DropdownMenuContent" data-sentry-source-file="ClassroomDropdownMenu.tsx">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>;
}
interface ClassroomDropdownMenuTriggerProps extends Omit<IconButtonProps, 'children' | 'intent' | 'size' | 'variant'> {
  icon?: 'vertical' | 'horizontal';
  intent?: IconButtonProps['intent'];
  preventDefault?: boolean;
  variant?: IconButtonProps['variant'];
}
export const ClassroomDropdownMenuTrigger = forwardRef<IconButtonElement, ClassroomDropdownMenuTriggerProps>(function ClassroomDropdownMenuTrigger({
  className,
  icon = 'vertical',
  intent = 'neutral',
  onClick,
  onPointerDown,
  preventDefault = false,
  variant = 'transparent',
  ...props
}, ref) {
  const Icon = icon === 'horizontal' ? OptionHorizontalIcon : OptionVerticalIcon;
  const handlePointerDown = (event: PointerEvent<IconButtonElement>) => {
    onPointerDown?.(event);
    event.stopPropagation();
  };
  const handleClick = (event: MouseEvent<IconButtonElement>) => {
    onClick?.(event);
    if (preventDefault) event.preventDefault();
    event.stopPropagation();
  };
  return <IconButton ref={ref} variant={variant} intent={intent} size="xs" className={className} onPointerDown={handlePointerDown} onClick={handleClick} {...props}>
      <Icon size="xs" fill={ICON_COLOR.neutral} />
    </IconButton>;
});
interface ClassroomDropdownMenuItemProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
  onSelect: () => void;
}
export function ClassroomDropdownMenuItem({
  children,
  className,
  disabled,
  icon,
  onSelect
}: ClassroomDropdownMenuItemProps) {
  return <DropdownMenuItem disabled={disabled} className={cn(MENU_ITEM_CLASS_NAME, className)} onPointerDown={stopMenuEvent} onClick={stopMenuEvent} onSelect={event => {
    event.stopPropagation();
    onSelect();
  }} data-sentry-element="DropdownMenuItem" data-sentry-component="ClassroomDropdownMenuItem" data-sentry-source-file="ClassroomDropdownMenu.tsx">
      <span className="flex min-w-0 items-center gap-2xs text-body-sm-medium text-default">
        {icon ? <span className="shrink-0 [&_svg]:shrink-0">{icon}</span> : null}
        <span className="truncate">{children}</span>
      </span>
    </DropdownMenuItem>;
}
export function ClassroomDropdownMenuSeparator({
  className
}: {
  className?: string;
}) {
  return <DropdownMenuSeparator className={cn('-mx-xs my-3xs bg-alternative', className)} data-sentry-element="DropdownMenuSeparator" data-sentry-component="ClassroomDropdownMenuSeparator" data-sentry-source-file="ClassroomDropdownMenu.tsx" />;
}